import { Box, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import React, { useEffect, useRef, useState } from "react";

const arrowSx = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  background: "rgba(255,255,255,0.9)",
  "&:hover": { background: "#fff" },
};

// Product photos for Fashion & Beauty stores: a large photo with arrows,
// swipe on touch screens, a counter and a thumbnail strip to jump between them.
function ProductGallery({ images, height = 460 }) {
  const [index, setIndex] = useState(0);
  const touchX = useRef(null);
  const thumbRefs = useRef([]);

  useEffect(() => setIndex(0), [images]);
  useEffect(() => {
    thumbRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [index]);

  const go = (next) =>
    setIndex((next + images.length) % images.length);

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          height,
          borderRadius: "40px 40px 0 0",
          overflow: "hidden",
          background: "#EEF0F2",
        }}
        onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
          touchX.current = null;
        }}
      >
        <Box
          sx={{
            display: "flex",
            height: "100%",
            transform: `translateX(-${index * 100}%)`,
            transition: "transform .3s ease",
          }}
        >
          {images.map((src, i) => (
            <Box
              key={`${i}-${src}`}
              component="img"
              src={src}
              alt=""
              draggable={false}
              sx={{
                flex: "0 0 100%",
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ))}
        </Box>
        <IconButton
          aria-label="Previous photo"
          onClick={() => go(index - 1)}
          sx={{ ...arrowSx, left: 16 }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          aria-label="Next photo"
          onClick={() => go(index + 1)}
          sx={{ ...arrowSx, right: 16 }}
        >
          <ChevronRightIcon />
        </IconButton>
        <Box
          sx={{
            position: "absolute",
            right: 20,
            bottom: 16,
            px: 1.25,
            py: 0.5,
            borderRadius: "12px",
            background: "rgba(0,0,0,0.55)",
          }}
        >
          <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>
            {`${index + 1}/${images.length}`}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          px: "40px",
          pt: 1.5,
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {images.map((src, i) => (
          <Box
            key={`thumb-${i}-${src}`}
            ref={(el) => (thumbRefs.current[i] = el)}
            component="button"
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Photo ${i + 1}`}
            sx={{
              flex: "0 0 auto",
              width: 72,
              height: 72,
              p: 0,
              borderRadius: "12px",
              overflow: "hidden",
              cursor: "pointer",
              border: "2px solid",
              borderColor: i === index ? "primary.main" : "transparent",
              opacity: i === index ? 1 : 0.7,
              background: "none",
            }}
          >
            <Box
              component="img"
              src={src}
              alt=""
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default ProductGallery;
